import { msg, plural, t } from '@lingui/core/macro';
import { z } from 'zod';

import { action, can, defineScreen } from '@/shared/screen/define';
import type { ActionPreview, ScreenContext } from '@/shared/screen/types';

import { CREATE_TEST_EMPLOYEE_PATH, DATAPREV_TEST_FLAG } from './create-test-employee.screen';

// Manifesto do "Excluir Autorização de Margem" (U23/U42). Arquivo leve. Mesmas condições do Criar CPF
// (`router.tsx:1317-1336`). O legado exclui sem confirmar (`DeleteAuthorizationContainer.tsx:78-118`);
// aqui é `destructive`, com prévia e "Sim, excluir" (U24, U39 §6).

export const DELETE_AUTHORIZATION_PATH = '/ccb/excluir-autorizacao';

const loadApi = () => import('./dataprev-employee.api');
const screenOnly = () => Promise.reject(new Error('dataprev-employee: ação da tela montada'));
// eslint-disable-next-line lingui/no-unlocalized-strings -- descrição do input do agente, em inglês (U33)
const cpf = z.string().regex(/^\d{11}$/).describe('CPF, 11 digits');

export const deleteAuthHistory = action({
  id: 'dataprevEmployee.deleteAuthorization.history',
  kind: 'navigate',
  label: msg`Histórico de Exclusões`,
  description: msg`Abre a lista dos últimos 10 CPFs que tiveram as autorizações de margem excluídas nesta sessão do navegador.`,
  run: ({ nav }) => nav.search({ overlay: 'historico' }),
});

export const deleteAuthGoCreate = action({
  id: 'dataprevEmployee.deleteAuthorization.goCreate',
  kind: 'navigate',
  label: msg`Criar CPF(s)`,
  description: msg`Abre a tela que cria CPFs de teste na Dataprev.`,
  run: ({ nav }) => nav.to(CREATE_TEST_EMPLOYEE_PATH),
});

export const deleteAuthAdd = action({
  id: 'dataprevEmployee.deleteAuthorization.add',
  kind: 'draft',
  label: msg`Adicionar`,
  description: msg`Põe um CPF (11 dígitos) na lista da tela dos CPFs que terão as autorizações excluídas. CPF repetido é ignorado. Nada é enviado.`,
  input: z.object({ cpf }),
  run: screenOnly,
});

export const deleteAuthRemove = action({
  id: 'dataprevEmployee.deleteAuthorization.remove',
  kind: 'draft',
  row: true,
  label: msg`Remover`,
  description: msg`Tira um CPF da lista da tela. Nada é enviado.`,
  input: z.object({ cpf }),
  run: screenOnly,
});

/** Prévia da exclusão: quantos e quais CPFs (o diálogo e o agente mostram o mesmo texto). */
export function deleteAuthorizationPreview(cpfs: readonly string[]): ActionPreview {
  const count = cpfs.length;
  const list = cpfs.map((item) => item.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')).join(', ');
  return {
    title: plural(count, { one: 'Excluir # autorização?', other: 'Excluir # autorizações?' }),
    detail: t`As autorizações de margem dos CPFs ${list} serão excluídas na Dataprev para que possam ser reutilizados.`,
    tone: 'destructive',
  };
}

export const deleteAuthDelete = action({
  id: 'dataprevEmployee.deleteAuthorization.delete',
  kind: 'destructive',
  label: msg`Excluir autorizações`,
  description: msg`Exclui na Dataprev as autorizações de margem dos CPFs informados, para que possam ser usados de novo nos testes. Não dá para desfazer. Pede a confirmação da pessoa.`,
  input: z.object({ cpfs: z.array(cpf).min(1).max(100) }),
  preview: (input) => deleteAuthorizationPreview(input.cpfs),
  run: async ({ input }) => (await loadApi()).deleteAuthorizations(input.cpfs),
});

export const deleteAuthorizationScreen = defineScreen({
  id: 'dataprevEmployee.deleteAuthorization',
  path: DELETE_AUTHORIZATION_PATH,
  title: msg`Excluir Autorização de Margem`,
  description: msg`Exclui na Dataprev as autorizações de margem de CPFs de teste escolhidos, para que possam ser usados de novo, e mostra o resultado de cada CPF.`,
  permission: can('CreditNote', 'Read'),
  access: { flag: DATAPREV_TEST_FLAG },
  place: { icon: 'shield-check', section: 'credit-note' },
  actions: [deleteAuthHistory, deleteAuthGoCreate, deleteAuthAdd, deleteAuthRemove, deleteAuthDelete],
});

/** O que o agente lê (U24, U46): quantos CPFs na lista e o último resultado, no idioma da tela. */
export function deleteAuthorizationContext(input: { readonly listed: number; readonly result: string | undefined }): ScreenContext {
  const listed = input.listed;
  const count = plural(listed, { one: '# CPF na lista.', other: '# CPFs na lista.' });
  const result = input.result;
  return {
    summary: [t`Excluir Autorização de Margem na Dataprev.`, count, result ? t`Último resultado: ${result}.` : ''].filter(Boolean).join(' '),
    fields: { listed: input.listed },
    sensitivity: 'restricted',
  };
}
